/**
 * One page per thing we have built, because a card cannot carry a build.
 *
 * The "what we built" list on each page is NOT written here. It is pulled from
 * the capability catalogue by product name, so a case study can never claim a
 * feature the catalogue does not have a source file for, and adding a capability
 * updates the case study for free.
 *
 * `situation` is the client's problem in their words, not ours. `approach` is
 * what we did about it, and `why` is the business reason it is that way. None of
 * them ever states a result the client would have to verify for us: what we built
 * is ours to claim, what it earned them is not.
 *
 * WHAT AND WHY, NEVER HOW (Tom, 2026-08-16). There is deliberately no list of
 * frameworks or services on these pages. A buyer does not choose a supplier on
 * which database they use, and telling them turns a sales page into a spec sheet.
 * Say what was added and what the owner was struggling with that made it worth
 * adding.
 */

import { BUILT_GROUPS, type BuiltItem } from "@/lib/built-catalogue";
import { storeLinksFor } from "@/lib/proof";
import type { SiteVideo } from "@/lib/site-videos";

/**
 * When these pages were published, taken from git history rather than guessed: every study was
 * written and added on 2026-08-12 in one pass. It is a real date, which matters because a
 * fabricated one is the same lie as a fabricated testimonial, and because AI-cited content
 * skews meaningfully fresher, so the date is doing work rather than decorating.
 *
 * Update it when a study is genuinely rewritten, not when a typo is fixed.
 */
export const CASE_STUDIES_PUBLISHED = "2026-08-12";

export type CaseStudy = {
  slug: string;
  name: string;
  label: "Client" | "Internal";
  businessType: string;
  /**
   * A shorter, plainer title for the SEARCH RESULT, when the product name alone does not
   * describe what the page is.
   *
   * `name` is the thing we called it and it stays the heading. But somebody typing "instant
   * quote tool" into Google does not know what "The Instant Quote Estimator" is, and a result
   * that only repeats a product name wins nothing at the bottom of page one. Set this when the
   * page ranks for what the build DOES rather than for what it is called. Leave it unset
   * otherwise: for a client case study the client's name is the better title.
   */
  seoTitle?: string;
  /** One line under the title, used on cards and as the meta description seed. */
  summary: string;
  situation: string;
  approach: readonly string[];
  /** The business reason it is built this way. Never how it is built. */
  why: string;
  liveHref?: string;
  /**
   * What the button to `liveHref` says, when the standard call to action is the wrong words.
   *
   * An internal link defaults to `OWNLY_OFFER.primaryCta`, which is right for a case study
   * about a client build. It is wrong for a page a stranger reached by searching for a tool:
   * they came to use the thing, and the button should say so.
   */
  liveLabel?: string;
  /** Name in the capability catalogue, when the product has entries there. */
  catalogueProduct?: string;
  /**
   * The service page this build is proof of, when more than one names it and the first one
   * declared is not the right answer.
   *
   * `servicesForCaseStudy` reads the service registry in declaration order, which is fine when
   * a study is cited once. The estimator is cited twice, and the earlier of the two is
   * `web-app-development`, which describes how it was built rather than what it does. Somebody
   * reading this page wants "quoting and estimating systems". Naming it here overrides the
   * order without unpicking the registry, and the check below refuses a service that does not
   * cite this study back, so the two halves cannot drift apart.
   */
  primaryServiceSlug?: string;
  /** App name in SHIPPED_APPS, when it is on the stores. */
  appName?: string;
  imageSrc: string;
  imageAlt: string;
  /**
   * A walkthrough of this build, roughly three minutes, hosted by us in `public/video/`.
   *
   * Say the same things this page says and in the same order: what the owner was struggling
   * with, what it does now, and why it is built that way. Never how. A video that starts
   * naming tools turns a sales page into a spec sheet just as surely as the copy would.
   *
   * Absent until Tom records it. A study with no video simply renders without one; there is
   * no "video coming soon" panel anywhere on this site.
   */
  video?: SiteVideo;
  /**
   * Questions this page answers, shown on the page and marked up as FAQPage.
   *
   * Most case studies do not need these: they are read by somebody who already knows why they
   * are here. A study that ranks for a search term is different, because the reader arrived
   * with a question rather than with us in mind, and answering it is what earns the click.
   *
   * Google's rule: the answer must be visible on the page and complete on its own. The
   * template renders them, so using this field satisfies that.
   */
  faqs?: readonly { question: string; answer: string }[];
  /**
   * The marketing site that sells this app, told as a section of the app's own page.
   *
   * WHY ONE PAGE AND NOT TWO (2026-09-28). Each ProFlow app used to have two case studies, one for
   * the app and one for its marketing site. Search Console had both app pages on "Crawled,
   * currently not indexed": two pages about the same client, same brand and the same sentence
   * shapes read as near duplicates, and Google kept neither. They are one build to the client, so
   * they are one page here, and the old `-marketing-site` addresses redirect to it permanently
   * (next.config.ts). `liveHref` on the study itself points at the site, because for an app the
   * website is the part a stranger can open without installing anything.
   */
  website?: CaseStudyWebsite;
};

export type CaseStudyWebsite = {
  /** The section heading here, and the card title wherever the site is shown on its own. */
  name: string;
  businessType: string;
  summary: string;
  situation: string;
  approach: readonly string[];
  why: string;
  /** Name in the capability catalogue, so the section lists what the site does from source. */
  catalogueProduct?: string;
  imageSrc: string;
  imageAlt: string;
};

/** The in-page anchor of the website section, on a study that has one. */
export const WEBSITE_SECTION_ID = "website";

export const CASE_STUDIES: readonly CaseStudy[] = [
  {
    slug: "the-dfc-experience",
    name: "The DFC Experience",
    label: "Client",
    /*
     * RE-RENDERED 2026-09-14 after #167 pulled the first cut for a torn hero. DFC's hero is an 81s
     * video streamed with its index at the end of the file, so the renderer's per-frame seek never
     * knew its duration and it played on wall-clock time while frames took seconds each to capture.
     * The reel engine now swaps a page's background video for a local, fully loaded copy and waits
     * for every seek before capturing (`videoOverrides` in content-engine/reels/dfc-experience.json).
     * The replacement also isolates the reel's caption fonts so they cannot replace the site's
     * own Fraunces face (command-center#88). Preserve both fixes on future recordings.
     * `npm run check:films` refuses this block if the two files are not committed.
     */
    video: {
      src: "/video/the-dfc-experience.mp4",
      poster: "/video/the-dfc-experience-poster.jpg",
      dimensions: { width: 1600, height: 1000 },
      length: "24 sec",
      description:
        "A short film of the DFC Experience site: the artist roster, the venues they work, and a booking that asks what a planner would ask.",
      uploadDate: "2026-09-14",
    },
    businessType: "Wedding and event entertainment",
    summary:
      "A premium booking site for DJs, hosts and live artists, editable end to end by the client.",
    situation:
      "Entertainment gets booked on feel and on proof, and neither survives a page of text. They needed a site that sells the night itself, and they needed to change it themselves, because a venue partnership or a new artist cannot wait for a developer to have a free afternoon.",
    approach: [
      "We built an image-led site around the acts rather than the services: artist pages with video that plays on hover, a venue partner roster, and a page for every area they actually travel to.",
      "The booking flow is multi-step on purpose. It asks the questions a planner would ask, files itself into the system they already run on, and keeps the rubbish out with bot protection rather than a captcha nobody can read.",
      "Everything visible is editable by their team through a built-in studio, with a draft mode so they can see a change before the public does. The homepage rotates by itself so a returning visitor does not see the same thing every week.",
    ],
    why: "The decision that shaped everything was handing the client the keys. A site an agency has to edit is a site that is always a month behind the business, and this one changes every time a new act or venue comes on.",
    liveHref: "https://thedfcexperience.co.uk/",
    catalogueProduct: "The DFC Experience",
    imageSrc: "/work/dfc-experience-preview.png",
    imageAlt:
      "The DFC Experience website, desktop view of the live entertainment booking site",
  },
  {
    slug: "proflow-academy",
    name: "ProFlow Academy",
    label: "Client",
    businessType: "Coaching app, iOS and Android",
    summary:
      "A posing coaching programme turned into a real product, live on both app stores, with a website in nine languages that sells it.",
    situation:
      "The coaching ran out of direct messages and scattered video links. Every new member meant re-sending the same files, and nobody could see who had watched what, or who had gone quiet.",
    approach: [
      "We built a native app for iOS and Android with role-based dashboards for admin, coaches and clients, so what you can do depends on who you are rather than which link you were sent.",
      "The programme itself became a video library with timed unlocks, so content arrives in the order the coaching intends rather than all at once on day one.",
      "Around it: custom check-in forms, progress photos and video, prepaid session balances, booking inside the app, community posts and replies, push notifications that reach the phone rather than an inbox, and one-tap translation for members who do not read English.",
    ],
    why: "Role-based from the start rather than one app with things hidden. What a coach can do and what a member can see are different jobs, and building that in at the beginning is the difference between adding a coach in a minute and rebuilding the app.",
    liveHref: "https://proflowacademy.co.uk/",
    catalogueProduct: "ProFlow Academy",
    appName: "ProFlow Academy",
    imageSrc: "/work/proflow-academy-preview.png",
    imageAlt: "ProFlow Academy coaching app sign-in screen preview",
    website: {
      name: "ProFlow Academy website",
      businessType: "Multilingual marketing site, nine languages",
      summary:
        "The front door that sells the programme before anyone downloads anything, in nine languages.",
      situation:
        "An app on a store page sells nothing. The programme needed somewhere to explain itself, to people in several countries, before the download was ever a question.",
      approach: [
        "We built the site in nine languages, with the coaching team, the packages and the proof laid out properly rather than crammed into a store listing.",
        "Checkout is handled inline, and every route on the site points at the same next step: create an account and start.",
        "English is the source of truth and every other language is kept level with it, so nobody lands on a page that gives up halfway through and reverts to English.",
        "Choosing a coach re-prices the whole page: every membership, package and single session shows that coach's own rate, and the checkout button follows. A coach who is not taking clients cannot be booked even from a link somebody kept from last month.",
        "Buying is one tap. There is no basket to build and no account to create first, and paying lands on a page that says what happens next and when the login arrives, rather than dropping them back on the homepage.",
      ],
      why: "Every price on the page follows the coach you pick, rather than one page per coach. Coaches charge differently and their availability changes weekly, and a page per coach is a page per coach to keep correct.",
      catalogueProduct: "ProFlow Academy Marketing Site",
      imageSrc: "/work/proflow-academy-marketing-preview.png",
      imageAlt: "ProFlow Academy marketing website, desktop view of the live site",
    },
  },
  {
    slug: "proflow-physiques",
    name: "ProFlow Physiques",
    label: "Client",
    businessType: "Coaching app, iOS and Android",
    summary:
      "A second app under the same brand, built for one to one coaching rather than a group programme, and the website that sells the coach.",
    situation:
      "The first ProFlow app worked, so they came back for a second, this time for a different business entirely: one to one physique coaching, where every client has their own plan and the coach needs to see progress week by week.",
    approach: [
      "Rather than bending the first app to fit a business it was not designed for, we built a separate product: separate stores, separate data, separate audience.",
      "Training, nutrition, supplements, cardio and body metrics live in one place, with numbers that cannot contradict each other, and plan templates so the same block is not rebuilt from scratch for every client.",
      "Weekly photo and video check-ins go straight to the coach, including from a phone with one bar of signal in a car park, which is where they are actually recorded. Video gets captions without paying per minute, and the coach can reply with a voice note attached to the exact exercise it is about.",
    ],
    why: "A second product rather than a mode inside the first. Group programmes and one to one coaching disagree about almost everything, and a single app trying to be both would have made the working one worse.",
    liveHref: "https://proflowphysiques.co.uk/",
    catalogueProduct: "ProFlow Physiques",
    appName: "ProFlow Physiques",
    imageSrc: "/work/proflow-physiques-preview.png",
    imageAlt: "ProFlow Physiques coaching app sign-in screen preview",
    website: {
      name: "ProFlow Physiques website",
      businessType: "Coaching marketing site",
      summary:
        "One coach, so the site sells the coach, and hands over qualified enquiries rather than clicks.",
      situation:
        "A one to one coach cannot take everybody, so the site's job is not traffic. It is making sure the people who do get in touch already understand how the coaching runs and what it costs them in effort.",
      approach: [
        "Real client results sit up front, followed by an honest account of how the coaching actually runs week to week, including the parts that are hard work.",
        "The path from reading to talking to the coach is short and there is only one of it, so nothing competes with the enquiry.",
        "Enquiries route straight to the coach rather than into a shared inbox that nobody owns.",
        "A price on the page can never link to a different price at checkout. A plan with a real checkout goes to checkout, a plan without one goes to the enquiry form, and nothing is allowed to charge a figure the page did not show. That is what lets the coach change prices the same day he decides to.",
        "Each of the eight programmes has a page of its own, so somebody searching for the specific thing they want lands on it, and the articles the coach writes inside his own app appear on the site by themselves.",
      ],
      why: "The site qualifies rather than converts. One coach with limited places is better served by fewer, better-briefed enquiries than by more of them, so it is honest about the work involved instead of hiding it.",
      catalogueProduct: "ProFlow Physiques Marketing Site",
      imageSrc: "/work/proflow-physiques-marketing-preview.png",
      imageAlt: "ProFlow Physiques marketing website, desktop view of the live site",
    },
  },
  {
    slug: "my-trusted-accountant",
    name: "My Trusted Accountant",
    label: "Client",
    businessType: "Accountancy practice, Leyland",
    summary:
      "A trust-led rebuild for a practice competing on credibility rather than price.",
    situation:
      "Accountants compete on trust, and the old site asked visitors to take that trust on faith. Services were listed in the language of the profession rather than the language of the person worrying about their tax return.",
    approach: [
      "We rebuilt the site to lead with credibility: clear service pages in plain English, transparent positioning, and no jargon a first-time client would have to look up.",
      "Every page ends in the same single next step, so an enquiry never depends on someone hunting for a contact form.",
      "Each service, bookkeeping, business accounts, VAT, tax returns and fractional finance director, has its own page, so somebody searching for the one thing they are anxious about finds a page about that.",
      "Client words run through the site rather than sitting on a testimonials tab nobody visits, and nothing is measured until a visitor has actually agreed to it, which matters more than usual for a practice that advises on compliance.",
    ],
    why: "Written for the worried, not the qualified. People choose an accountant when something is going wrong, and they choose the one that sounds like it understands them rather than the one that lists the most services.",
    liveHref: "https://mytrustedaccountant.co.uk",
    catalogueProduct: "My Trusted Accountant",
    imageSrc: "/work/my-trusted-accountant-preview.png",
    imageAlt: "My Trusted Accountant website, desktop view of the live marketing site",
  },
  {
    slug: "elevate-physique-coaching",
    name: "Elevate Physique Coaching",
    label: "Internal",
    businessType: "Our founder's own online coaching business",
    summary:
      "The website for our founder's own coaching business: what the coaching is, who it is for, and how to apply.",
    situation:
      "Elevate is our founder's own coaching business, not a client, so its website is where we try ideas on ourselves first. Coaches lose enquiries when a site never quite says what the coaching is, who it suits and what it asks of you. Interested people leave to think about it and do not come back.",
    approach: [
      "We built the site around offer clarity: the programme, the proof, and a single path to apply.",
      "A swipeable transformation showcase carries the proof, and the application form qualifies before it books, so conversations start with people who already fit.",
      "A free tool answers the visitor's question before asking them for anything, and half-finished applications are not lost, so an enquiry that stopped at question four is still a person the coach can follow up.",
    ],
    why: "The form qualifies before it books. A coach's time is the product, so a conversation with someone who does not fit costs more than the enquiry was worth.",
    liveHref: "https://www.elevatephysiquecoaching.com",
    catalogueProduct: "Elevate Physique Coaching",
    imageSrc: "/work/elevate-physique-preview.png",
    imageAlt: "Elevate Physique Coaching website, desktop view of the live marketing site",
  },
  {
    slug: "meridian-supply-co",
    name: "Meridian Supply Co.",
    label: "Client",
    businessType: "Construction and commercial supply",
    summary:
      "A launch site for a new supply business with two divisions, built so that asking for any product is one step.",
    situation:
      "Meridian was a brand new company with no website, no web address and two very different sets of buyers: developers and contractors buying construction materials, and warehouses, factories and commercial premises buying everything from pallet wrap to safety footwear. It needed to show the full range on day one, and to make it easy to ask for something that is not on any list.",
    approach: [
      "Each division has its own page, construction materials on one and warehouse, factory and commercial supplies on the other, with the product range laid out in full so a buyer can see at a glance whether Meridian covers what they need.",
      "Every product on those pages opens the enquiry form with that item already filled in. The form asks what a buyer expects to be asked: quantity, delivery date, postcode, and a place to attach a schedule or drawing.",
      "One form handles four different requests, a quote, a general conversation, sourcing something specific and a trade account, so every enquiry arrives already sorted. Spam is kept out by a bot check rather than a puzzle, and attached files are stored privately rather than passed around by email.",
      "We also registered the domain and set up the business inbox the enquiries go to, so the company went from a name to a working web address and email in one step.",
    ],
    why: "A sourcing business sells on range and on answering quickly, not on a fixed catalogue. So the site shows the breadth, says plainly that the lists are not the limit, and makes \"can you get this for us?\" the easiest question to send.",
    liveHref: "https://meridian-supply.co.uk/",
    imageSrc: "/work/meridian-supply-preview.png",
    imageAlt: "Meridian Supply Co. website, desktop view of the live supply site",
  },
  {
    slug: "growthsync",
    name: "GrowthSync",
    label: "Internal",
    businessType: "Our own B2B networking product",
    summary:
      "A live networking product we designed, built, shipped to both stores, and run every day.",
    situation:
      "The strongest thing we can show a client is not a portfolio. It is a product we carry the pager for. GrowthSync is ours: we chose the problem, built it, put it through Apple review, and answer for it when something breaks at seven in the morning.",
    approach: [
      "A web app, an iOS app and an Android app from one codebase, with matching, discovery, communities, opportunities and member-hosted events.",
      "Events run end to end: tickets sold through Stripe, passes that live in Apple and Google Wallet and update themselves if the venue changes, transfer to a colleague who can go instead, and scanning on the door that a host can hand to somebody else.",
      "It ships in twenty languages, and the build fails if a single string is left in English. Members can export everything they have and delete their account, without emailing anyone to ask.",
    ],
    why: "We keep the pager for this one on purpose. Anyone can show a portfolio; running your own product in the stores is the part that proves we can live with what we build rather than hand it over and leave.",
    liveHref: "https://growth-sync.com/",
    catalogueProduct: "GrowthSync",
    appName: "GrowthSync",
    imageSrc: "/work/growthsync-site-preview.png",
    imageAlt: "GrowthSync site preview of the SaaS networking app marketing site",
  },
] as const;

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((c) => c.slug === slug);
}

/**
 * The catalogue entries belonging to one product, in catalogue order.
 * This is what a case study's "what we built" list renders from.
 */
export function builtItemsFor(product: string | undefined): BuiltItem[] {
  if (!product) return [];
  return BUILT_GROUPS.flatMap((g) => g.items.filter((i) => i.product === product));
}

/**
 * A case study as the site shows it on a card: the whole build, or just its website.
 *
 * `href` is set on the website view, which points at the website section of the one page rather
 * than at a page of its own. There used to be a separate `-marketing-site` page for that, and
 * the landing pages about websites for coaches still want to show the site rather than the app.
 */
export type CaseStudyCard = CaseStudy & { href?: string };

/**
 * A study's website section, shaped as a card of its own. Undefined for a study without one.
 * No `appName`, so the card does not grow store badges for an app it is not about.
 */
export function websiteCardOf(study: CaseStudy): CaseStudyCard | undefined {
  const site = study.website;
  if (!site) return undefined;
  return {
    ...study,
    name: site.name,
    businessType: site.businessType,
    summary: site.summary,
    imageSrc: site.imageSrc,
    imageAlt: site.imageAlt,
    catalogueProduct: site.catalogueProduct,
    appName: undefined,
    video: undefined,
    href: `/work/${study.slug}#${WEBSITE_SECTION_ID}`,
  };
}

/**
 * Resolve a reference from a page registry: a plain slug is the whole build, and
 * `slug#website` is that build's website section on its own. Unknown references are undefined,
 * so each caller keeps its own loud failure.
 */
export function resolveCaseStudyRef(ref: string): CaseStudyCard | undefined {
  const [slug, part] = ref.split("#");
  const study = getCaseStudy(slug);
  if (!study) return undefined;
  if (part === undefined) return study;
  return part === WEBSITE_SECTION_ID ? websiteCardOf(study) : undefined;
}

/** Store links for a case study, when the product is on the stores. */
export function caseStudyStoreLinks(study: CaseStudy) {
  return study.appName ? storeLinksFor(study.appName) : undefined;
}
